import { useEffect, useMemo, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { EditableCollectionBinding, useCollectionBinding } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import {
  GalleryLightbox,
  GallerySharedStyles,
  type GalleryImage,
} from './_galleryShared';

/**
 * The property photo gallery — a single, unified masonry grid that shows
 * every photo in the property `photo_gallery` collection, apartment and
 * community shots alike. The collection binding has no `title` filter, so
 * uncategorized photos surface here too (previously they were split across
 * separate "Apartment" and "Community" sections joined by a separator band).
 *
 * The slot is still named `GalleryApartments` for backwards compatibility
 * with existing pages and saved content, but it now renders the whole gallery.
 * Clicking any tile opens the shared lightbox at that photo.
 */

/**
 * Number of masonry columns for the current viewport (3 desktop / 2 tablet
 * and mobile). SSG renders the desktop default; the effect corrects it on
 * mount and on resize. Kept as a hook so the distribution memo can react to it.
 */
function useMasonryColumns(): number {
  const [columns, setColumns] = useState(3);

  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth;
      setColumns(w < 1024 ? 2 : 3);
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, []);

  return columns;
}

interface Tile {
  img: GalleryImage;
  index: number;
}

export function GalleryApartments() {
  const images = useCollectionBinding<GalleryImage>('galleryapartments.images', {
    collection: 'photo_gallery',
    defaultLimit: 48,
  });

  const columnCount = useMasonryColumns();

  // Round-robin distribution so photos read left-to-right by row (photo 0 in
  // the first column, 1 in the second, 2 in the third, 3 back in the first…),
  // matching a shortest-column masonry's visual order while staying pure CSS.
  const columns = useMemo<Tile[][]>(() => {
    const buckets: Tile[][] = Array.from({ length: columnCount }, () => []);
    images.forEach((img, index) => {
      buckets[index % columnCount].push({ img, index });
    });
    return buckets;
  }, [images, columnCount]);

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  if (images.length === 0) {
    return (
      <section className="bg-white">
        <div className="gallery-constrained py-12 text-center text-[var(--vibe-color-text-muted)]">
          <p className="font-body text-lg">Gallery images will be displayed here.</p>
        </div>
        <GallerySharedStyles />
      </section>
    );
  }

  return (
    <section className="bg-white">
      <div className="gallery-constrained pt-8 md:pt-12 pb-12">
        <EditableCollectionBinding
          bindingPath="galleryapartments.images"
          label="Gallery Photos"
        >
          <div className="gallery-masonry">
            {columns.map((col, colIndex) => (
              <div className="gallery-masonry__col" key={colIndex}>
                {col.map(({ img, index }, rowIndex) => {
                  // Checkerboard stagger: landscape (4:3) on even cells, vertical
                  // (3:4) on odd cells, so rows read L-V-L then flip to V-L-V.
                  const isVertical = (rowIndex + colIndex) % 2 === 1;
                  return (
                    <button
                      type="button"
                      key={img.image || index}
                      className={`gallery-masonry__item gallery-item group ${
                        isVertical
                          ? 'gallery-masonry__item--vertical'
                          : 'gallery-masonry__item--landscape'
                      }`}
                      onClick={() => setSelectedIndex(index)}
                      aria-label={`View photo ${index + 1}`}
                    >
                      <VibesitesImage
                        url={img.image}
                        alt={img.alt_text || img.title || ''}
                        sizes="(min-width: 1024px) 33vw, 50vw"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </EditableCollectionBinding>
      </div>

      <GalleryLightbox
        images={images}
        selectedIndex={selectedIndex}
        onClose={() => setSelectedIndex(null)}
        onChange={setSelectedIndex}
      />

      <GallerySharedStyles />

      <style>{`
        /* Row-order masonry: N side-by-side flex columns, each stacking its
           tiles at their natural aspect ratio. Used on every breakpoint —
           2 columns on mobile/tablet, 3 on desktop — instead of a carousel. */
        .gallery-masonry {
          display: flex;
          align-items: flex-start;
          gap: 0.5rem;
        }
        .gallery-masonry__col {
          display: flex;
          flex: 1 1 0;
          min-width: 0;
          flex-direction: column;
          gap: 0.5rem;
        }
        .gallery-masonry__item {
          display: block;
          width: 100%;
          margin: 0;
          padding: 0;
          border: none;
          background: none;
          border-radius: 2px;
          overflow: hidden;
          cursor: pointer;
          line-height: 0;
        }
        /* Fixed-aspect tiles produce the staggered L/V checkerboard; images are
           cropped to fill via object-cover. */
        .gallery-masonry__item--landscape { aspect-ratio: 4 / 3; }
        .gallery-masonry__item--vertical { aspect-ratio: 3 / 4; }
        .gallery-masonry__item img {
          display: block;
          width: 100%;
          height: 100%;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'GalleryApartments',
  displayName: 'Photo Gallery',
  tokens: {},
  collectionBindings: {
    'galleryapartments.images': {
      label: 'Gallery Photos',
      collection: 'photo_gallery',
      defaultLimit: 48,
      description:
        'Every photo from the property photo gallery — both apartment and community shots — shown in one masonry grid.',
    },
  },
};

(GalleryApartments as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default GalleryApartments;
